import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "hidden-reviewer-ids";

const loadFromLocalStorage = (): Set<string> => {
  if (typeof window === "undefined" || !window.localStorage) {
    return new Set();
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Set();
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return new Set(parsed);
    }
  } catch (error) {
    console.error("Error parsing hidden reviewer IDs from localStorage:", error);
  }
  return new Set();
};

export function useReviewersFilter() {
  const [hiddenReviewerIds, setHiddenReviewerIds] = useState<Set<string>>(new Set());

  // Initialize from localStorage on mount
  useEffect(() => {
    const loadedIds = loadFromLocalStorage();
    if (loadedIds.size > 0) {
      setHiddenReviewerIds(loadedIds);
    }
  }, []);

  // Persist to localStorage when state changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (hiddenReviewerIds.size > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(hiddenReviewerIds)));
    } else {
      // Remove from localStorage if empty
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [hiddenReviewerIds]);

  const toggleReviewerVisibility = useCallback((reviewerId: string) => {
    setHiddenReviewerIds((prev) => {
      const next = new Set(prev);
      if (next.has(reviewerId)) {
        next.delete(reviewerId);
      } else {
        next.add(reviewerId);
      }
      return next;
    });
  }, []);

  return {
    hiddenReviewerIds,
    toggleReviewerVisibility,
  };
}
